/**
 * Audit tab (Phase 5 Action 49).
 *
 * mc_audit_log entries scoped to this tenant. Answers "what did we
 * (PowerMove employees) do to this customer?" and pairs with the
 * Activity tab: audit rows and their sibling `operator.*` events share
 * a correlation_id, so an investigation can jump between the two.
 *
 * Details are rendered as collapsible JSON — same rationale as the
 * Activity tab; an operator-facing debug view doesn't need to be
 * pretty.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useOutletContext } from 'react-router-dom';
import { fetchJson, formatDateTime, type TenantContext } from './shared';

interface AuditEntry {
  id: string;
  operatorId: string;
  operatorEmail: string | null;
  action: string;
  reason: string;
  details: Record<string, unknown>;
  correlationId: string;
  requestIp: string | null;
  createdAt: string;
}

export default function Audit() {
  const { tenant } = useOutletContext<TenantContext>();

  const q = useQuery({
    queryKey: ['tenant-audit', tenant.id],
    queryFn: () => fetchJson<{ data: { entries: AuditEntry[] } }>(`/api/tenants/${tenant.id}/audit`),
  });

  if (q.isLoading) {
    return <div className="rounded-md border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">Loading audit log…</div>;
  }
  if (q.isError || !q.data) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
        Could not load audit log: {q.error instanceof Error ? q.error.message : 'unknown error'}
      </div>
    );
  }

  const entries = q.data.data.entries;

  return (
    <section className="mb-6 rounded-md border border-slate-200 bg-white">
      <header className="flex items-center justify-between border-b border-slate-200 px-6 py-3">
        <h2 className="text-sm font-semibold text-slate-900">Operator actions</h2>
        <span className="text-xs text-slate-500">Showing {entries.length} most recent</span>
      </header>
      {entries.length === 0 ? (
        <div className="px-6 py-8 text-center text-sm text-slate-500">
          No operator actions have been recorded for this tenant.
        </div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {entries.map((e) => <AuditRow key={e.id} entry={e} />)}
        </ul>
      )}
    </section>
  );
}

function AuditRow({ entry }: { entry: AuditEntry }) {
  const [open, setOpen] = useState(false);
  const hasDetails = entry.details && Object.keys(entry.details).length > 0;
  return (
    <li className="px-6 py-4 text-sm">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-mono text-slate-900">{entry.action}</span>
        <span className="text-xs text-slate-500">
          by {entry.operatorEmail ?? <span className="font-mono">{entry.operatorId.slice(0, 8)}</span>}
        </span>
        <span className="ml-auto text-xs text-slate-500">{formatDateTime(entry.createdAt)}</span>
      </div>
      <div className="mt-1 text-slate-700">{entry.reason}</div>
      <div className="mt-1 flex items-center gap-3 text-xs text-slate-400 font-mono">
        <span>corr {entry.correlationId.slice(0, 8)}</span>
        {entry.requestIp && <span>ip {entry.requestIp}</span>}
        {hasDetails && (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="text-slate-500 hover:text-slate-700 underline"
          >
            {open ? 'Hide details' : 'Show details'}
          </button>
        )}
      </div>
      {hasDetails && open && (
        <pre className="mt-2 overflow-x-auto rounded-md bg-slate-50 p-2 text-xs text-slate-700 font-mono">
          {JSON.stringify(entry.details, null, 2)}
        </pre>
      )}
    </li>
  );
}
